import { useState } from 'react';
import { Plus, Trash2, Server as ServerIcon, Pencil, ChevronDown, ChevronRight, ArrowRight } from 'lucide-react';
import { Section } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import {
  Button,
  Card,
  CardBody,
  Input,
  Badge,
  IconButton,
  Switch,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import {
  useUpdateLoadBalancer,
  useCertificates,
  type LoadBalancer,
  type LbListener,
  type LbServer,
} from '@/api/opennebula';
import { ListenerAdvancedFields, HealthCheckEditor, AclRuleEditor } from './ListenerAdvanced';
import { primaryNic, withNames } from './listener-helpers';

// HAProxy listeners: TCP (L4) + HTTP/HTTPS (L7). UDP is NOT a HAProxy protocol — L4
// UDP/TCP forwarding is a separate feature (LVS/IPVS) under the Port forwarding tab.
const PROTOCOLS = ['TCP', 'HTTP', 'HTTPS'];

export function ListenerManager({ serviceId, lb }: { serviceId: string; lb: LoadBalancer }) {
  const { show } = useToast();
  const listeners = lb.metadata?.listeners ?? [];
  const [editing, setEditing] = useState<{ index: number; listener: LbListener } | null>(null);
  const [delIndex, setDelIndex] = useState<number | null>(null);

  const update = useUpdateLoadBalancer(serviceId, lb.id, {
    onSuccess: () => { show({ tone: 'success', title: 'Listeners updated' }); setEditing(null); setDelIndex(null); },
    onError: () => show({ tone: 'danger', title: 'Update failed' }),
  });

  const save = (next: LbListener[]) => update.mutate({ lb: { listeners: withNames(next) }, network: primaryNic(lb) });

  const onSaveListener = (listener: LbListener, index: number) => {
    const next = [...listeners];
    if (index < 0) next.push(listener);
    else next[index] = listener;
    save(next);
  };
  const onDelete = (index: number) => save(listeners.filter((_, i) => i !== index));

  return (
    <Section
      title="Listeners"
      description="Front-end ports and the backend servers they balance traffic to."
      actions={
        <Button size="sm" startIcon={<Plus size={15} />} onClick={() => setEditing({ index: -1, listener: { protocol: 'TCP', port: '80', servers: [] } })}>
          Add listener
        </Button>
      }
    >
      {listeners.length === 0 ? (
        // Populated state is a custom card grid — wrap empty in Card so both
        // states share a consistent bordered frame.
        <Card><CardBody><EmptyState icon={<ServerIcon size={28} />} title="No listeners" description="Add a listener to start balancing traffic." /></CardBody></Card>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
          {listeners.map((l, i) => {
            const count = l.servers?.length ?? 0;
            return (
              <Card key={i} style={{ overflow: 'hidden' }}>
                {/* Header — the frontend: protocol + listening port, routing to N backends */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--vn-space-3)', padding: 'var(--vn-space-3) var(--vn-space-4)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', minWidth: 0 }}>
                    <Badge tone={l.protocol === 'HTTPS' ? 'success' : l.protocol === 'HTTP' ? 'info' : 'neutral'} appearance="subtle">{l.protocol}</Badge>
                    <span style={{ fontWeight: 600, fontFamily: 'var(--vn-font-mono)' }}>:{l.port}</span>
                    {l.certificate && <Badge tone="neutral" appearance="subtle" size="sm">TLS</Badge>}
                    <ArrowRight size={14} style={{ color: 'var(--vn-fg-subtle)', flexShrink: 0 }} />
                    <span style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)', whiteSpace: 'nowrap' }}>
                      {count} backend{count === 1 ? '' : 's'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 'var(--vn-space-1)', flexShrink: 0 }}>
                    <IconButton variant="ghost" size="sm" aria-label="Edit listener" onClick={() => setEditing({ index: i, listener: l })}><Pencil size={15} /></IconButton>
                    <IconButton variant="ghost" size="sm" aria-label="Delete listener" onClick={() => setDelIndex(i)}><Trash2 size={15} /></IconButton>
                  </div>
                </div>

                {/* Backend servers the listener balances across */}
                <div style={{ borderTop: '1px solid var(--vn-border-subtle)', background: 'var(--vn-bg-subtle)', padding: '6px var(--vn-space-4)' }}>
                  {count === 0 ? (
                    <span style={{ color: 'var(--vn-fg-subtle)', fontSize: 'var(--vn-font-size-sm)' }}>No backend servers.</span>
                  ) : (
                    <div style={{ display: 'grid' }}>
                      {l.servers!.map((s, si) => (
                        <div
                          key={si}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 'var(--vn-space-2)',
                            padding: '6px 0',
                            borderTop: si ? '1px solid var(--vn-border-subtle)' : 'none',
                            fontSize: 'var(--vn-font-size-sm)',
                          }}
                        >
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)', minWidth: 0 }}>
                            <ServerIcon size={13} style={{ color: 'var(--vn-fg-subtle)', flexShrink: 0 }} />
                            <code style={{ fontFamily: 'var(--vn-font-mono)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.host}:{s.port}</code>
                          </span>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-1)', flexShrink: 0 }}>
                            {s.weight != null && <Badge tone="neutral" appearance="subtle" size="sm">weight {s.weight}</Badge>}
                            {s.backup && <Badge tone="warning" appearance="subtle" size="sm">backup</Badge>}
                            {s.tls_encrypt && <Badge tone="neutral" appearance="subtle" size="sm">TLS</Badge>}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {editing && (
        <ListenerEditor
          serviceId={serviceId}
          listener={editing.listener}
          saving={update.isPending}
          onCancel={() => setEditing(null)}
          onSave={(l) => onSaveListener(l, editing.index)}
        />
      )}
      <ConfirmDialog
        open={delIndex != null}
        onOpenChange={(o) => !o && setDelIndex(null)}
        title="Delete listener?"
        description="Traffic on this port will stop being balanced."
        confirmLabel="Delete"
        destructive
        loading={update.isPending}
        onConfirm={() => delIndex != null && onDelete(delIndex)}
      />
    </Section>
  );
}

function ListenerEditor({
  serviceId,
  listener,
  saving,
  onCancel,
  onSave,
}: {
  serviceId: string;
  listener: LbListener;
  saving: boolean;
  onCancel: () => void;
  onSave: (l: LbListener) => void;
}) {
  const { data: certs = [] } = useCertificates(serviceId);
  const [protocol, setProtocol] = useState(String(listener.protocol ?? 'TCP'));
  const [port, setPort] = useState(String(listener.port ?? '80'));
  const [certificate, setCertificate] = useState(listener.certificate ?? '');
  const [servers, setServers] = useState<LbServer[]>(listener.servers ?? []);
  // Advanced fields (timeouts/L7/sticky/headers), health-check and ACL rules.
  const [adv, setAdv] = useState<LbListener>(listener);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const isHttp = protocol === 'HTTP' || protocol === 'HTTPS';
  const isHttps = protocol === 'HTTPS';
  // Backend server protocol is derived from the listener, not chosen per server
  // (matches @console). HTTP/HTTPS listeners proxy as HTTP; everything else as TCP.
  const serverProtocol = isHttp ? 'HTTP' : 'TCP';

  const addServer = () => setServers((s) => [...s, { host: '', port: port, backup: false }]);
  const setServer = (i: number, patch: Partial<LbServer>) => setServers((s) => s.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const delServer = (i: number) => setServers((s) => s.filter((_, j) => j !== i));
  const patchAdv = (patch: Partial<LbListener>) => setAdv((a) => ({ ...a, ...patch }));

  const valid = !!port && servers.every((s) => s.host && s.port);

  const submit = () => {
    const next: LbListener = {
      ...listener,
      ...adv,
      protocol,
      port,
      servers,
      certificate: isHttps ? certificate || undefined : undefined,
      rules: isHttp ? adv.rules : undefined,
    };
    onSave(next);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent size="lg">
        <DialogHeader><DialogTitle>{listener.name ? `Edit ${listener.name}` : 'Add listener'}</DialogTitle></DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--vn-space-3)' }}>
              <Field label="Protocol">
                {() => (
                  <Select value={protocol} onValueChange={setProtocol}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{PROTOCOLS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Port">{(p) => <Input {...p} type="number" value={port} onChange={(e) => setPort(e.target.value)} />}</Field>
            </div>

            {protocol === 'HTTPS' && (
              <Field label="TLS certificate">
                {() => (
                  <Select value={certificate} onValueChange={setCertificate}>
                    <SelectTrigger><SelectValue placeholder="Select a certificate" /></SelectTrigger>
                    <SelectContent>{certs.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.id}{c.type ? ` · ${c.type}` : ''}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </Field>
            )}

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--vn-space-2)' }}>
                <span style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)' }}>Backend servers</span>
                <Button size="sm" variant="secondary" startIcon={<Plus size={14} />} onClick={addServer}>Add server</Button>
              </div>
              {servers.length === 0 ? (
                <EmptyState icon={<ServerIcon size={20} />} title="No servers yet" description="Add a backend server to route traffic to." />
              ) : (
                <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                  {servers.map((s, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'grid',
                        // minmax(0,…) lets the input tracks shrink to fit the dialog instead of
                        // forcing horizontal overflow; everything stays on one tidy row.
                        gridTemplateColumns: 'minmax(0, 1.8fr) minmax(0, 0.8fr) minmax(0, 0.9fr) minmax(0, 0.9fr) auto auto',
                        gap: 'var(--vn-space-2)',
                        alignItems: 'center',
                      }}
                    >
                      <Input style={{ minWidth: 0 }} placeholder="Host e.g. 10.0.0.10" value={s.host ?? ''} onChange={(e) => setServer(i, { host: e.target.value })} />
                      <Input style={{ minWidth: 0 }} type="number" placeholder="Port" value={String(s.port ?? '')} onChange={(e) => setServer(i, { port: e.target.value })} />
                      {/* Backend protocol is derived from the listener (HTTP/HTTPS → HTTP, else TCP) and
                          not separately editable — mirrors @console's disabled select. */}
                      <Select value={serverProtocol} disabled>
                        <SelectTrigger style={{ minWidth: 0 }}><SelectValue /></SelectTrigger>
                        <SelectContent>{['TCP', 'UDP', 'HTTP', 'HTTPS'].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input style={{ minWidth: 0 }} type="number" placeholder="Weight" value={s.weight == null ? '' : String(s.weight)} onChange={(e) => setServer(i, { weight: e.target.value ? Number(e.target.value) : null })} />
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--vn-font-size-sm)', whiteSpace: 'nowrap' }}>
                        <Switch checked={!!s.backup} onCheckedChange={(v) => setServer(i, { backup: v })} /> backup
                      </label>
                      <IconButton variant="ghost" size="sm" aria-label="Remove server" onClick={() => delServer(i)}><Trash2 size={14} /></IconButton>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Advanced settings (collapsible) */}
            <button
              type="button"
              onClick={() => setShowAdvanced((v) => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-1)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-default)' }}
            >
              {showAdvanced ? <ChevronDown size={15} /> : <ChevronRight size={15} />} Advanced settings
            </button>
            {showAdvanced && (
              <div style={{ display: 'grid', gap: 'var(--vn-space-4)', paddingLeft: 'var(--vn-space-2)', borderLeft: '2px solid var(--vn-border-default)' }}>
                <ListenerAdvancedFields listener={adv} onChange={patchAdv} isHttp={isHttp} isHttps={isHttps} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-2)' }}>Health check</div>
                  <HealthCheckEditor value={adv.health_check} onChange={(hc) => patchAdv({ health_check: hc })} isHttp={isHttp} />
                </div>
                {isHttp && (
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', marginBottom: 'var(--vn-space-2)' }}>ACL routing rules</div>
                    <AclRuleEditor value={adv.rules ?? []} onChange={(rules) => patchAdv({ rules })} />
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button disabled={!valid || saving} onClick={submit}>Save listener</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
